/** quick-260807-j2u — UTC-safe wrapper around Angular's DatePipe.
 *
 * The 2026-08-07 team-QA walkthrough reported upload timestamps on /submissions reading off
 * by the browser's UTC offset. Same root cause as the quick-260804-ui1 relative-time defect:
 * the API serializes some timestamps WITHOUT a zone designator (`2026-08-07T14:30:00`), and
 * both `new Date(...)` and Angular's `| date:` treat a zone-less string as LOCAL time. A UTC
 * value from the server therefore renders shifted by the machine's offset.
 *
 * `parseInstant` already encodes the API's contract ("zone-less means UTC") plus SQL Server's
 * 7-digit fractional seconds; this pipe just routes DatePipe through it. Use `| utcDate:'short'`
 * anywhere a server timestamp is displayed — it is a drop-in replacement for `| date:'short'`.
 *
 * Note the *output* is still rendered in the viewer's local zone (that is the desired behavior:
 * a Nevada user should see Pacific wall-clock); what is fixed is the INPUT interpretation.
 */
import { DatePipe } from '@angular/common';
import { Pipe, PipeTransform } from '@angular/core';
import { parseInstant } from './relative-time';

@Pipe({
  name: 'utcDate',
  standalone: true,
  pure: true,
})
export class UtcDatePipe implements PipeTransform {
  // DatePipe is not providedIn:'root', and a standalone pipe has no reliable injector of its
  // own to add providers to — instantiate it directly. 'en-US' matches the app's LOCALE_ID
  // default, and any caller needing another locale can pass one through.
  private readonly datePipe = new DatePipe('en-US');

  transform(
    value: string | Date | null | undefined,
    format = 'mediumDate',
    timezone?: string,
    locale?: string,
  ): string | null {
    const ms = parseInstant(value);
    if (ms === null) {
      return '';
    }
    return this.datePipe.transform(new Date(ms), format, timezone, locale);
  }
}
